/**
 * Date utilities for consistent date handling across the app.
 *
 * An expense date is a plain calendar date, captured from the user's local
 * timezone at the moment of entry, and anchored there permanently: it carries
 * no time and no timezone, so it never shifts if the user later travels or
 * changes their clock. It stays the date they entered it on, for display and
 * for every calculation.
 *
 * The invariant that guarantees this: dates are YYYY-MM-DD strings everywhere —
 * in the database (a Postgres `date` column), over the wire, and in React state.
 * They are converted to a display format only at the point of rendering, via
 * formatForDisplay(), and never converted back.
 *
 * Never use `new Date('YYYY-MM-DD')` on one of these: that parses as UTC
 * midnight and renders as the previous day in any negative-offset timezone.
 * Use parseLocalDate() instead.
 */

/**
 * Formats a Date object as a YYYY-MM-DD string using local time.
 * Use this when saving dates to the database.
 * @param {Date} date - The date to format (defaults to now)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const toLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parses a YYYY-MM-DD string as a local date.
 * Avoids UTC interpretation issues that occur with new Date('YYYY-MM-DD').
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} Date object in local time
 */
export const parseLocalDate = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a YYYY-MM-DD string for display using the user's locale.
 *
 * Returns an em dash for a missing date rather than "Invalid Date": income that
 * has been earned but not yet paid legitimately has no cash-intake date, so an
 * empty value reaches here as a normal case, not an error.
 *
 * @param {string|null|undefined} dateString - Date string in YYYY-MM-DD format
 * @param {string} locale - Locale for formatting (defaults to 'en-US')
 * @returns {string} Formatted date string for display
 */
export const formatForDisplay = (dateString, locale = 'en-US') => {
  if (!dateString) return '—';
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString(locale);
};

/**
 * Gets the date range for a given month/year as YYYY-MM-DD strings.
 * @param {number} month - Month (1-12)
 * @param {number} year - Full year (e.g., 2025)
 * @returns {{ startDate: string, endDate: string }}
 */
export const getMonthDateRange = (month, year) => {
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { startDate, endDate };
};
